import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const dict = {
  nl: {
    nav: { home: "Home", airports: "Luchthavens", book: "Boeken", login: "Inloggen", register: "Registreren", dashboard: "Dashboard", admin: "Admin", logout: "Uitloggen" },
    hero: {
      eyebrow: "Premium Nederlandse Taxiservice",
      title_a: "Reizen in",
      title_b: "Luxe & Vertrouwen",
      subtitle: "Professionele chauffeurs. Vaste tarieven. 24/7 beschikbaar door heel Nederland en daarbuiten.",
      cta_book: "Direct boeken",
      cta_call: "Bel ons",
    },
    booking: {
      title: "Boek uw rit",
      subtitle: "Vul de gegevens in voor een directe prijsopgave",
      pickup: "Ophaaladres",
      destination: "Bestemming",
      date: "Datum", time: "Tijd",
      passengers: "Passagiers", luggage: "Bagage",
      oneway: "Enkele reis", return: "Retour",
      return_date: "Retourdatum", return_time: "Retourtijd",
      name: "Naam", email: "E-mail", phone: "Telefoon", notes: "Opmerkingen",
      quote: "Bereken prijs",
      confirm: "Boeking bevestigen",
      total: "Totaal",
      distance: "Afstand", duration: "Reistijd",
      airport_select: "Luchthaven (optioneel)", none: "Geen",
      pay: "Betaal & Bevestig",
      reviewing: "Even geduld...",
    },
    adv: {
      title: "Waarom Sam Taxi",
      items: [
        ["Professionele chauffeurs", "Ervaren, in uniform en discreet."],
        ["Vaste prijzen", "Geen verrassingen. U weet vooraf wat u betaalt."],
        ["Luchthaventransfers", "Schiphol, Eindhoven, Rotterdam, Brussels, Düsseldorf."],
        ["24/7 service", "Wij rijden dag en nacht, het hele jaar door."],
        ["Veilige betalingen", "iDEAL, Visa, Mastercard, Apple Pay, Google Pay via Mollie."],
      ],
    },
    reviews: { title: "Wat onze klanten zeggen" },
    contact: { title: "Contact", form_name: "Naam", form_email: "E-mail", form_message: "Bericht", send: "Verstuur", whatsapp: "WhatsApp", call: "Bellen" },
    footer: { privacy: "Privacy", terms: "Voorwaarden", cookies: "Cookies", company: "Bedrijf", legal: "Juridisch", rights: "Alle rechten voorbehouden." },
  },
  en: {
    nav: { home: "Home", airports: "Airports", book: "Book", login: "Login", register: "Register", dashboard: "Dashboard", admin: "Admin", logout: "Logout" },
    hero: {
      eyebrow: "Premium Dutch Taxi Service",
      title_a: "Travel in",
      title_b: "Luxury & Trust",
      subtitle: "Professional chauffeurs. Fixed prices. Available 24/7 across the Netherlands and beyond.",
      cta_book: "Book now",
      cta_call: "Call us",
    },
    booking: {
      title: "Book your ride",
      subtitle: "Fill in the details for an instant quote",
      pickup: "Pickup address",
      destination: "Destination",
      date: "Date", time: "Time",
      passengers: "Passengers", luggage: "Luggage",
      oneway: "One way", return: "Return",
      return_date: "Return date", return_time: "Return time",
      name: "Name", email: "Email", phone: "Phone", notes: "Notes",
      quote: "Get quote",
      confirm: "Confirm booking",
      total: "Total",
      distance: "Distance", duration: "Duration",
      airport_select: "Airport (optional)", none: "None",
      pay: "Pay & Confirm",
      reviewing: "Please wait...",
    },
    adv: {
      title: "Why Sam Taxi",
      items: [
        ["Professional drivers", "Experienced, uniformed and discreet."],
        ["Fixed prices", "No surprises. Know your price upfront."],
        ["Airport transfers", "Schiphol, Eindhoven, Rotterdam, Brussels, Düsseldorf."],
        ["24/7 service", "We drive day and night, all year round."],
        ["Secure payments", "iDEAL, Visa, Mastercard, Apple Pay, Google Pay via Mollie."],
      ],
    },
    reviews: { title: "What our clients say" },
    contact: { title: "Contact", form_name: "Name", form_email: "Email", form_message: "Message", send: "Send", whatsapp: "WhatsApp", call: "Call" },
    footer: { privacy: "Privacy Policy", terms: "Terms & Conditions", cookies: "Cookie Policy", company: "Company", legal: "Legal", rights: "All rights reserved." },
  },
};

const SUPPORTED_LANGS = ["nl", "en"];
const I18nCtx = createContext({ lang: "nl", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem("samtaxi_lang");
    return SUPPORTED_LANGS.includes(stored) ? stored : "nl";
  });

  const setLang = useCallback((l) => {
    const safe = SUPPORTED_LANGS.includes(l) ? l : "nl";
    setLangState(safe);
    localStorage.setItem("samtaxi_lang", safe);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = "ltr";
  }, [lang]);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let v = dict[lang];
      for (const p of parts) {
        if (v == null) return path;
        v = v[p];
      }
      return v ?? path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}
